import { useCallback, useEffect, useRef, useState } from 'react'
import { MessageSquare, X, Stethoscope } from 'lucide-react'
import { useMe } from '../lib/me.js'
import { isDoctorOnly } from '../lib/role.js'
import { useChatInbox, useUnread, snippet } from '../lib/chat.js'
import { openChatDock, isChatDockOpenFor, emitChatMessage } from '../lib/chatDock.js'

/**
 * The reach half of patient chat.
 *
 * A chat that only exists inside the patient page is a message board people
 * forget to check: the front desk writes about patient A while the doctor is in
 * patient B's chart, and if she does not see it within ~30 seconds she walks to
 * the room anyway — at which point the feature has earned nothing and has
 * created a new place for instructions to go unread.
 *
 * So every session subscribes to `chat_inbox/{user_id}` and toasts what Xano
 * has already decided is theirs (thread participants + the visit's doctor).
 * Mounted in BOTH layouts — the employee shell and the patient module — since
 * only one of them is ever rendered at a time.
 *
 * It also owns the single `autoLoad` of the shared unread store, so the badges
 * elsewhere do not each kick their own first request.
 */
export default function ChatToaster() {
  const me = useMe()
  const [toasts, setToasts] = useState([])
  const timers = useRef(new Map())
  useUnread({ autoLoad: true })

  const drop = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.key !== id))
    const h = timers.current.get(id)
    if (h) { clearTimeout(h); timers.current.delete(id) }
  }, [])

  useChatInbox(me, (p) => {
    // Hand it to any open dock FIRST — this component owns the only subscription
    // to chat_inbox, so the bus is how a dock hears about a message at all.
    emitChatMessage(p)
    // Already reading that thread? It just refetched; a toast would only cover
    // the input. See isChatDockOpenFor in lib/chatDock.js.
    if (isChatDockOpenFor(p.patient_id)) return
    const key = `${p.id}-${p.patient_id}`
    setToasts((t) => {
      if (t.some((x) => x.key === key)) return t
      // Three at a time. A stack taller than that stops being read and starts
      // covering the page it is interrupting.
      return [...t.slice(-2), { ...p, key }]
    })
    timers.current.set(key, setTimeout(() => drop(key), 12000))
  })

  useEffect(() => () => { for (const h of timers.current.values()) clearTimeout(h) }, [])

  if (!toasts.length) return null

  // Opens the dock over whatever you are looking at — no navigation, no new tab.
  // It used to link to /patient/:id/...?chat=1 in a new tab, which charged a full
  // page load for a two-line message and left the front desk collecting one more
  // tab per message. ChatDockHost is mounted in both layouts and answers this.
  const openDock = (p) => {
    drop(p.key)
    openChatDock({ patientId: p.patient_id, patientName: p.fullname || '' })
  }

  return (
    <div className="fixed z-[60] bottom-4 right-4 flex flex-col gap-2 w-[min(360px,calc(100vw-2rem))]">
      {toasts.map((p) => (
        <div
          key={p.key}
          className="rounded-xl bg-white shadow-xl shadow-slate-900/10 ring-1 ring-slate-200 p-3 animate-ss-pop-in"
          role="status"
        >
          <div className="flex items-start gap-2.5">
            <span className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-none ${
              p.author_role === 'doctor' ? 'bg-violet-50 text-violet-600' : 'bg-brand-50 text-brand-600'
            }`}>
              {p.author_role === 'doctor' ? <Stethoscope size={15} strokeWidth={2} /> : <MessageSquare size={15} strokeWidth={2} />}
            </span>
            <div className="min-w-0 flex-1">
              {/* Patient FIRST. The message is meaningless without knowing who
                  it is about, and that is the one thing a toast must never
                  make the reader guess. */}
              <div className="text-[13.5px] font-semibold text-slate-900 truncate">
                {p.fullname || 'ผู้ป่วย'}
                {p.HN && <span className="ml-1.5 font-mono text-[11.5px] font-normal text-slate-400">HN {p.HN}</span>}
              </div>
              <div className="text-[13px] text-slate-600 mt-0.5 break-words leading-snug">
                <span className="text-slate-400">{p.author_name || '—'}: </span>
                {snippet(p.body, 120)}
              </div>
              <button
                type="button"
                onClick={() => openDock(p)}
                className="inline-block mt-1.5 text-[12.5px] font-medium text-brand-700 hover:text-brand-800"
              >
                เปิดข้อความ →
              </button>
            </div>
            <button
              type="button"
              onClick={() => drop(p.key)}
              aria-label="ปิด"
              className="w-6 h-6 flex items-center justify-center rounded-md text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-none"
            >
              <X size={13} strokeWidth={2.25} />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
